/**
 * UsageMeters — the billing page's hero: live usage vs. the plan's limits for
 * seats, storage, WhatsApp credits, and AI credits.
 *
 * Laid out as a 2-up grid of meter tiles (not a flat list) so each metric gets
 * room to breathe: a big used/limit headline, a tone-aware bar (ok < 70% →
 * warn 70–90% → bad > 90%), and a footer that surfaces the data a founder
 * actually acts on — seats free, storage free, and for credit wallets the
 * balance *remaining* (which includes top-ups, so it can exceed the monthly
 * bundle) plus the renewal cadence. The bar always renders a visible track, so
 * a metric at 0% reads as "nothing used yet", never as a missing meter.
 */
import { Users, HardDrive, MessageSquare, Sparkles } from "lucide-react";
import { cn } from "@flint/ui";
import { FormCard } from "../form-card.tsx";

export interface BillingUsage {
  seats: { used: number; limit: number };
  storage: { usedBytes: string; limitBytes: string };
  whatsapp: { used: number; bundled: number; balance: number };
  ai: { used: number; monthlyAllocation: number; balance: number };
}

type Tone = "ok" | "warn" | "bad";

function toneFor(pct: number): Tone {
  if (pct > 90) return "bad";
  if (pct >= 70) return "warn";
  return "ok";
}

const FILL: Record<Tone, string> = { ok: "bg-ok", warn: "bg-warn", bad: "bg-bad" };
const PILL: Record<Tone, string> = {
  ok: "bg-ok-50 text-ok",
  warn: "bg-warn-50 text-warn",
  bad: "bg-bad-50 text-bad",
};
const CHIP: Record<Tone, string> = {
  ok: "bg-sky-50 text-sky-700",
  warn: "bg-warn-50 text-warn",
  bad: "bg-bad-50 text-bad",
};

function nf(n: number): string {
  return n.toLocaleString("en-IN");
}

function formatBytes(bytesStr: string | number): string {
  const n = typeof bytesStr === "number" ? bytesStr : Number(bytesStr);
  if (!Number.isFinite(n) || n <= 0) return "0 GB";
  const gb = n / 1024 ** 3;
  if (gb >= 1) return `${gb >= 10 ? Math.round(gb) : gb.toFixed(1)} GB`;
  const mb = n / 1024 ** 2;
  if (mb >= 1) return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

interface Meter {
  icon: typeof Users;
  label: string;
  /** One short, plain-language line demystifying an abstract metric. */
  hint?: string;
  /** Emphasised numerator (e.g. "5", "1.2 GB"). */
  used: string;
  /** Denominator incl. unit (e.g. "10 seats", "20 GB", "1,000 / mo"). */
  limit: string;
  pct: number;
  /** The line a founder acts on — seats free, balance left, etc. */
  footer: string;
}

function MeterTile({ icon: Icon, label, hint, used, limit, pct, footer }: Meter) {
  const tone = toneFor(pct);
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className="rounded-md border border-line p-4">
      <div className="flex items-start gap-2.5">
        <span className={cn("inline-grid h-8 w-8 shrink-0 place-items-center rounded-md", CHIP[tone])}>
          <Icon size={15} strokeWidth={2} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-tight text-ink">{label}</span>
          {hint ? <span className="mt-0.5 block text-xs leading-snug text-muted">{hint}</span> : null}
        </span>
        <span className={cn("shrink-0 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums", PILL[tone])}>
          {clamped}%
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums leading-none text-ink">{used}</span>
        <span className="text-sm tabular-nums text-muted">/ {limit}</span>
      </div>

      <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-raised">
        <span
          className={cn("block h-full rounded-full transition-all", FILL[tone])}
          style={{ width: `${Math.max(clamped, clamped > 0 ? 2 : 0)}%` }}
        />
      </div>

      <p className="mt-2 text-xs text-muted">{footer}</p>
    </div>
  );
}

export function UsageMeters({ usage, showAi = true, showWhatsapp = true }: { usage: BillingUsage; showAi?: boolean; showWhatsapp?: boolean }) {
  const seatsFree = Math.max(0, usage.seats.limit - usage.seats.used);
  const usedB = Number(usage.storage.usedBytes);
  const limitB = Number(usage.storage.limitBytes);
  const storageFree = Math.max(0, limitB - usedB);

  const meters: Meter[] = [
    {
      icon: Users,
      label: "Team members",
      used: nf(usage.seats.used),
      limit: `${nf(usage.seats.limit)} seats`,
      pct: usage.seats.limit > 0 ? (usage.seats.used / usage.seats.limit) * 100 : 0,
      footer: seatsFree > 0 ? `${nf(seatsFree)} seat${seatsFree === 1 ? "" : "s"} available` : "All seats in use",
    },
    {
      icon: HardDrive,
      label: "Storage",
      used: formatBytes(usedB),
      limit: formatBytes(limitB),
      pct: limitB > 0 ? (usedB / limitB) * 100 : 0,
      footer: `${formatBytes(storageFree)} free`,
    },
  ];

  if (showWhatsapp) {
    meters.push({
      icon: MessageSquare,
      label: "WhatsApp credits",
      hint: "Messages Flint sends for you on WhatsApp",
      used: nf(usage.whatsapp.used),
      limit: `${nf(usage.whatsapp.bundled)} / mo`,
      pct: usage.whatsapp.bundled > 0 ? (usage.whatsapp.used / usage.whatsapp.bundled) * 100 : 0,
      footer: `${nf(usage.whatsapp.balance)} credits left · renews monthly`,
    });
  }

  if (showAi) {
    meters.push({
      icon: Sparkles,
      label: "AI credits",
      hint: "Flint AI's work — briefings, replies, extraction",
      used: nf(usage.ai.used),
      limit: `${nf(usage.ai.monthlyAllocation)} / mo`,
      pct: usage.ai.monthlyAllocation > 0 ? (usage.ai.used / usage.ai.monthlyAllocation) * 100 : 0,
      footer: `${nf(usage.ai.balance)} credits left · renews monthly`,
    });
  }

  return (
    <FormCard
      icon={<HardDrive size={14} strokeWidth={2} aria-hidden />}
      title="Usage this period"
      description="What you're using against your plan's limits."
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {meters.map((m) => (
          <MeterTile key={m.label} {...m} />
        ))}
      </div>
    </FormCard>
  );
}
